import { CONSTANTS } from './constants.js';

// 48-timers forecast for optimal planlægning og bedre arbitrage
export class ForecastManager {
  constructor() {
    this.forecast = [];
    this.strategy = null;
  }

  // Load next 48 hours of data
  loadForecast(currentHour, dataProvider) {
    this.forecast = [];

    for (let i = 0; i < 48; i++) {
      const hourIndex = currentHour + i;
      if (hourIndex < 8783) {
        // Handle both function and object data providers
        let data;
        if (typeof dataProvider === 'function') {
          data = dataProvider(hourIndex);
        } else if (dataProvider && typeof dataProvider === 'object') {
          // If it's an object with methods, get the data from each method
          data = {
            spotPrice: dataProvider.getSpotPrice ? dataProvider.getSpotPrice(hourIndex) : 0,
            solarMWh: dataProvider.getSolarMW ? dataProvider.getSolarMW(hourIndex) : 0,
            wasteHeatMW: dataProvider.getWasteHeatMW ? dataProvider.getWasteHeatMW(hourIndex) : 0,
            demandMW: dataProvider.getDemandMW ? dataProvider.getDemandMW(hourIndex) : 0
          };
        }
        
        if (data) {
          this.forecast.push({
            hour: hourIndex,
            ...data
          });
        }
      }
    }
    
    this.analyzeAndPlan();
  }
  
  // Analyze forecast and create strategy
  analyzeAndPlan() {
    if (this.forecast.length === 0) return;
    
    // Find key metrics
    const avgPrice = this.forecast.reduce((sum, h) => sum + h.spotPrice, 0) / this.forecast.length;
    const minPrice = Math.min(...this.forecast.map(h => h.spotPrice));
    const maxPrice = Math.max(...this.forecast.map(h => h.spotPrice));
    const totalDemand = this.forecast.reduce((sum, h) => sum + h.demandMW, 0);
    const totalSolar = this.forecast.reduce((sum, h) => sum + h.solarMWh, 0);
    const totalWaste = this.forecast.reduce((sum, h) => sum + h.wasteHeatMW, 0);
    
    // Find cheap and expensive hours
    const cheapHours = this.forecast.filter(h => h.spotPrice < CONSTANTS.PRICE_THRESHOLDS.cheap);
    const expensiveHours = this.forecast.filter(h => h.spotPrice > CONSTANTS.PRICE_THRESHOLDS.expensive);
    
    // Determine strategy
    this.strategy = {
      avgPrice,
      minPrice,
      maxPrice,
      totalDemand,
      totalSolar,
      totalWaste,
      
      // Strategic decisions
      shouldChargeHTES: cheapHours.length > 0 && minPrice < CONSTANTS.PRICE_THRESHOLDS.very_cheap,
      shouldMaximizeSolar: totalSolar > totalDemand * 0.5,
      shouldPrioritizeWaste: totalWaste > totalDemand * 0.3,
      
      // Time-based recommendations
      bestChargeHours: cheapHours.sort((a,b)=>a.spotPrice-b.spotPrice).map(h => h.hour).slice(0, 3),
      bestDischargeHours: expensiveHours.sort((a,b)=>b.spotPrice-a.spotPrice).map(h => h.hour).slice(0, 3),
      // Battery arbitrage: Find cheapest and most expensive hour OF DAY (0-23) in the 24h window
      // Vi skal finde time på dagen, ikke absolut hour index
      bestBatteryChargeHour: this.forecast.reduce((min, h) => 
        h.spotPrice < (min?.spotPrice ?? Infinity) ? h : min, null)?.hour ?? null,
      bestBatteryDischargeHour: this.forecast.reduce((max, h) => 
        h.spotPrice > (max?.spotPrice ?? -Infinity) ? h : max, null)?.hour ?? null,
      
      // Storage targets
      recommendedLTESLevel: this.calculateRecommendedLTESLevel(),
      recommendedHTESLevel: this.calculateRecommendedHTESLevel()
    };
  }

  getStrategy() {
    return this.strategy;
  }
  
  // Calculate recommended LTES level based on forecast - PROACTIVE
  calculateRecommendedLTESLevel() {
    if (!this.forecast.length) return 0.5;
    
    // Check next 12 hours for better planning
    const next6Hours = this.forecast.slice(0, 6);
    const next12Hours = this.forecast.slice(0, 12);
    
    const demandNext6 = next6Hours.reduce((sum, h) => sum + h.demandMW, 0);
    const demandNext12 = next12Hours.reduce((sum, h) => sum + h.demandMW, 0);
    const wasteNext6 = next6Hours.reduce((sum, h) => sum + h.wasteHeatMW, 0);
    const wasteNext12 = next12Hours.reduce((sum, h) => sum + h.wasteHeatMW, 0);
    const solarNext6 = next6Hours.reduce((sum, h) => sum + h.solarMWh, 0);
    
    // Calculate net demand (demand minus cheap sources)
    const netDemandNext6 = Math.max(0, demandNext6 - wasteNext6 - solarNext6);
    const netDemandNext12 = Math.max(0, demandNext12 - wasteNext12);
    
    // PROACTIVE STRATEGY:
    // If high net demand coming, keep LTES fuller
    if (netDemandNext6 > 20) {
      return 0.8; // Keep 80% full for high demand periods
    }
    
    // If lots of waste heat coming, keep space available
    if (wasteNext6 > 30) {
      return 0.25; // Keep only 25% to maximize waste heat acceptance
    }
    
    // If balanced, maintain moderate level
    if (Math.abs(demandNext6 - wasteNext6) < 10) {
      return 0.45; // Balanced state
    }
    
    // Default based on net demand projection
    const recommendedLevel = Math.min(0.9, Math.max(0.2, 0.5 + (netDemandNext12 - 50) * 0.01));
    return recommendedLevel;
  }
  
  // Calculate recommended HTES level based on forecast - PROACTIVE ARBITRAGE
  calculateRecommendedHTESLevel() {
    if (!this.forecast.length) return 0.5;

    // Look for ALL price opportunities in next 48h
    const cheapHours = this.forecast.filter(h => h.spotPrice < CONSTANTS.PRICE_THRESHOLDS.cheap);
    const expensiveHours = this.forecast.filter(h => h.spotPrice > CONSTANTS.PRICE_THRESHOLDS.expensive);
    
    // Calculate price spread potential
    if (cheapHours.length > 0 && expensiveHours.length > 0) {
      const avgCheap = cheapHours.reduce((sum, h) => sum + h.spotPrice, 0) / cheapHours.length;
      const avgExpensive = expensiveHours.reduce((sum, h) => sum + h.spotPrice, 0) / expensiveHours.length;
      const priceSpread = avgExpensive - avgCheap;
      
      // Strong arbitrage opportunity
      if (priceSpread > 200) {
        // Are we before cheap hours? Keep low to buy
        const nextCheapHour = cheapHours[0]?.hour;
        const nextExpensiveHour = expensiveHours[0]?.hour;
        
        if (nextCheapHour && (!nextExpensiveHour || nextCheapHour < nextExpensiveHour)) {
          return 0.2; // Keep very low to maximize cheap charging
        } else {
          return 0.8; // Keep high to be ready for discharge
        }
      }
      
      // Moderate arbitrage
      if (priceSpread > 100) {
        return 0.5; // Balanced for flexibility
      }
    }
    
    // Check for emergency heat needs
    const next6Hours = this.forecast.slice(0, 6);
    const demandNext6 = next6Hours.reduce((sum, h) => sum + h.demandMW, 0);
    const wasteNext6 = next6Hours.reduce((sum, h) => sum + h.wasteHeatMW, 0);
    
    if (demandNext6 > wasteNext6 + 20) {
      return 0.7; // Keep higher for potential turbine operation
    }
    
    return 0.5; // Default 50%
  }
  
  // Get recommendation for current hour
  getHourlyRecommendation(currentHour) {
    if (!this.strategy) return null;
    
    const currentData = this.forecast.find(h => h.hour === currentHour);
    if (!currentData) return null;
    
    // Battery arbitrage: Check if we are at the best charge/discharge hour in our window
    // Vi skal sammenligne med de absolutte hour indices fra strategy
    const shouldChargeBattery = currentHour === this.strategy.bestBatteryChargeHour;
    const shouldDischargeBattery = currentHour === this.strategy.bestBatteryDischargeHour;
    
    return {
      // Charging recommendations
      shouldChargeHTES: this.strategy.bestChargeHours.includes(currentHour),
      shouldChargeLTES: currentData.spotPrice < this.strategy.avgPrice,
      shouldAcceptAllWaste: true, // Always accept waste heat at 150 DKK/MWh
      shouldBuySolar: currentData.solarMWh > 0 && currentData.spotPrice < CONSTANTS.PRICE_THRESHOLDS.cheap,
      
      // Discharging recommendations
      shouldRunTurbine: this.strategy.bestDischargeHours.includes(currentHour) ||
                       currentData.spotPrice > CONSTANTS.PRICE_THRESHOLDS.expensive,
      // Battery arbitrage - direkte sammenligning med forecast window
      shouldChargeBattery: shouldChargeBattery,
      shouldDischargeBattery: shouldDischargeBattery,
      
      // Target levels
      targetLTESLevel: this.strategy.recommendedLTESLevel,
      targetHTESLevel: this.strategy.recommendedHTESLevel,
      
      // Price context
      priceContext: this.getPriceContext(currentData.spotPrice),
      
      // Forecast summary
      nextHighPrice: this.getNextHighPriceHour(currentHour),
      nextLowPrice: this.getNextLowPriceHour(currentHour)
    };
  }
  
  // Get price context
  getPriceContext(price) {
    if (price < 0) return 'NEGATIVE';
    if (price < CONSTANTS.PRICE_THRESHOLDS.very_cheap) return 'VERY_CHEAP';
    if (price < CONSTANTS.PRICE_THRESHOLDS.cheap) return 'CHEAP';
    if (price < CONSTANTS.PRICE_THRESHOLDS.expensive) return 'NORMAL';
    if (price < CONSTANTS.PRICE_THRESHOLDS.very_expensive) return 'EXPENSIVE';
    return 'VERY_EXPENSIVE';
  }
  
  // Find next high price hour
  getNextHighPriceHour(currentHour) {
    const future = this.forecast.filter(h => h.hour > currentHour);
    const highPrice = future.find(h => h.spotPrice > CONSTANTS.PRICE_THRESHOLDS.expensive);
    return highPrice ? highPrice.hour : null;
  }
  
  // Find next low price hour
  getNextLowPriceHour(currentHour) {
    const future = this.forecast.filter(h => h.hour > currentHour);
    const lowPrice = future.find(h => h.spotPrice < CONSTANTS.PRICE_THRESHOLDS.cheap);
    return lowPrice ? lowPrice.hour : null;
  }
  
  // Get demand forecast
  getDemandForecast(hours = 6) {
    const next = this.forecast.slice(0, hours);
    return {
      total: next.reduce((sum, h) => sum + h.demandMW, 0),
      average: next.reduce((sum, h) => sum + h.demandMW, 0) / next.length,
      peak: Math.max(...next.map(h => h.demandMW))
    };
  }
  
  // Get available resources forecast
  getResourcesForecast(hours = 6) {
    const next = this.forecast.slice(0, hours);
    return {
      solar: next.reduce((sum, h) => sum + h.solarMWh, 0),
      wasteHeat: next.reduce((sum, h) => sum + h.wasteHeatMW, 0),
      avgPrice: next.reduce((sum, h) => sum + h.spotPrice, 0) / next.length
    };
  }
}

export default ForecastManager;